import { SiteIcon } from './SiteBrand';
import { flushSync } from "react-dom";
import { type ReactNode, type MouseEvent, type PointerEvent as ReactPointerEvent, useEffect, useMemo, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker&inline";
import { drawingObjectUrl, evidenceObjectUrl } from "./legacy-api";
import { imagePoint, layoutPins, type MapPin, type PdfPin } from "./drawing-layout";
import { drawingPdfWithPins } from "./drawing-pdf";
import type { Drawing } from "./OriginalApp";
GlobalWorkerOptions.workerPort = new PdfWorker();
type Id = string;

export function CommentPdfPreview({ url, name }: { url: string; name: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pageNumber, setPageNumber] = useState(1), [pages, setPages] = useState(0), [zoom, setZoom] = useState(1);
  const [message, setMessage] = useState('Opening PDF…');
  useEffect(() => {
    let active = true;
    let task: ReturnType<typeof getDocument> | undefined;
    setMessage('Opening PDF…');
    void fetch(url).then(response => response.arrayBuffer()).then(async bytes => {
      if (!active) return;
      task = getDocument({data:new Uint8Array(bytes),isEvalSupported:false,useWorkerFetch:false});
      const document = await task.promise;
      if (!active) return;
      if (!document.numPages) { setMessage('This PDF has no pages to preview.'); return; }
      setPages(document.numPages);
      const page = await document.getPage(pageNumber);
      if (!active || !canvas.current) return;
      const viewport = page.getViewport({scale:1.6}), context = canvas.current.getContext('2d',{alpha:false});
      if (!context) throw new Error('PDF preview unavailable');
      canvas.current.width = Math.ceil(viewport.width); canvas.current.height = Math.ceil(viewport.height);
      await page.render({canvas:canvas.current,canvasContext:context,viewport}).promise;
      if (active) setMessage('');
    }).catch(() => {if(active)setMessage('Preview unavailable. You can download the PDF below.');});
    return () => {active=false;void task?.destroy();};
  },[url,pageNumber]);
  return <div className="comment-pdf-preview">
    {pages > 0 && <div className="comment-pdf-controls"><button type="button" disabled={pageNumber<=1} onClick={()=>setPageNumber(value=>value-1)}>Previous</button><span>Page {pageNumber} of {pages}</span><button type="button" disabled={pageNumber>=pages} onClick={()=>setPageNumber(value=>value+1)}>Next</button><button type="button" aria-label="Zoom PDF out" disabled={zoom<=1} onClick={()=>setZoom(value=>value-.5)}>−</button><button type="button" aria-label="Zoom PDF in" disabled={zoom>=3} onClick={()=>setZoom(value=>value+.5)}>+</button></div>}
    {message && <p role="status">{message}</p>}
    <div className="comment-pdf-page"><canvas ref={canvas} aria-label={`Page ${pageNumber} of ${name}`} style={{width:`${zoom*100}%`,visibility:message?'hidden':'visible'}} /></div>
  </div>;
}

export function PhotoTile({ photoId, label }: { photoId: Id | null; label: string }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => { setUrl(""); setFailed(false); let active = true; if (photoId) void evidenceObjectUrl(photoId).then((value) => { if (active) setUrl(value); }).catch(() => { if (active) setFailed(true); }); return () => { active = false; }; }, [photoId]);
  return photoId && url ? <img className="evidence-photo" src={url} alt={label} onError={() => { setFailed(true); setUrl(""); }} /> : <div className="evidence-placeholder" data-media-state={photoId && !failed ? "loading" : failed ? "error" : "ready"}><SiteIcon name="photo" /><small>{failed ? "Photo unavailable" : label}</small></div>;
}

function PdfDrawingPreview({ drawing, pageNumber, onAspect, onOpenFull }: { drawing: Drawing; pageNumber: number; onAspect: (aspect: number) => void; onOpenFull:()=>void }) {
  const [url, setUrl] = useState("");
  const [rendered, setRendered] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let active = true; setRendered(false); setFailed(false);
    let documentTask: ReturnType<typeof getDocument> | null = null;
    void drawingObjectUrl(drawing.id).then(async (value) => {
      const blob = await (await fetch(value)).blob();
      if (!active) return;
      setUrl(value); setRendered(false); setFailed(false);
      const bytes = new Uint8Array(await blob.arrayBuffer());
      documentTask = getDocument({ data: bytes, isEvalSupported: false, useWorkerFetch: false });
      const document = await documentTask.promise;
      if (!active) return;
      const page = await document.getPage(pageNumber);
      if (!active || !canvasRef.current) return;
      const viewport = page.getViewport({ scale: 1.6 });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("Drawing canvas unavailable");
      onAspect(viewport.width / viewport.height);
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      if (active) setRendered(true);
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; void documentTask?.destroy(); };
  }, [attempt, drawing.id, pageNumber, onAspect]);
  return <div data-media-state={failed ? "error" : rendered ? "ready" : "loading"} className={`pdf-drawing-preview ${rendered ? "ready" : "loading"}`}>
    <canvas aria-label={`Page ${pageNumber} of ${drawing.fileName}`} ref={canvasRef} />
    {!rendered && <div><span className={failed ? "pdf-file-badge" : "pdf-spinner"}>{failed ? "PDF" : ""}</span><strong>{failed ? "Preview did not open" : `Opening ${drawing.fileName}…`}</strong>{failed && <button className="pdf-retry" onClick={() => setAttempt((value) => value + 1)} type="button">Retry preview</button>}</div>}
    {url && <button className="open-full-pdf" type="button" onClick={event=>{event.stopPropagation();onOpenFull();}}>Open full PDF with pins ↗</button>}
  </div>;
}

function DrawingImage({ drawing, onAspect }: { drawing: Drawing; onAspect: (aspect: number) => void }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true; setUrl(""); setFailed(false);
    void drawingObjectUrl(drawing.id).then((value) => { if (active) setUrl(value); }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [drawing.id]);
  return failed ? <div data-media-state="error" className="drawing-loading">Drawing unavailable</div> : url ? <img src={url} alt={drawing.fileName} onError={() => setFailed(true)} onLoad={(event) => onAspect(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)} /> : <div data-media-state="loading" className="drawing-loading">Opening drawing…</div>;
}

export function DrawingCanvas({ drawing, page = 1, x = null, y = null, coordinateSpace = "legacy", number = "1", pins, onOpenPin, onMark, print = false, leadingControls, onAspectChange, pdfPins }: {
  drawing: Drawing; page?: number; x?: number | null; y?: number | null; coordinateSpace?: "image" | "legacy"; number?: string;
  pins?: MapPin[]; onOpenPin?: (id: string) => void; onMark?: ((x: number, y: number) => void) | undefined; print?: boolean;
  leadingControls?:ReactNode; onAspectChange?:(aspect:number)=>void; pdfPins?:PdfPin[];
}) {
  const [aspect, setAspect] = useState(0);
  const [pdfMessage,setPdfMessage]=useState("");
  const pdfUrls=useRef<string[]>([]);
  useEffect(()=>()=>{pdfUrls.current.forEach(url=>URL.revokeObjectURL(url));},[]);
  useEffect(()=>{onAspectChange?.(aspect);},[aspect,onAspectChange]);
  const frameRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => { setAspect(0); setZoom(1); }, [drawing.id, page]);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const update = () => setSize({ width: frame.clientWidth, height: frame.clientHeight });
    const observer = new ResizeObserver(update); observer.observe(frame); update();
    const beforePrint = () => flushSync(update);
    window.addEventListener("beforeprint",beforePrint);
    return () => { observer.disconnect(); window.removeEventListener("beforeprint",beforePrint); };
  }, []);
  const points = pins ?? (x != null && y != null ? [{ id: "current", number, x: x / 10000, y: y / 10000, space: coordinateSpace }] : []);
  async function openFullPdf() {
    const tab=window.open('about:blank','_blank');
    if(tab) {tab.opener=null;tab.document.title='Preparing drawing with pins';tab.document.body.textContent='Preparing the full drawing with snag pins…';}
    setPdfMessage('Preparing PDF with pins…');
    try {
      const url=await drawingObjectUrl(drawing.id), blob=await (await fetch(url)).blob();
      const bytes=await drawingPdfWithPins(new Uint8Array(await blob.arrayBuffer()),pdfPins ?? points.map(pin=>({...pin,page})));
      const output=URL.createObjectURL(new Blob([new Uint8Array(bytes).buffer],{type:'application/pdf'}));pdfUrls.current.push(output);
      if(tab && !tab.closed)tab.location.replace(output);
      else {const link=document.createElement('a');link.href=output;link.download=drawing.fileName.replace(/\.pdf$/i,'')+'-with-snag-pins.pdf';link.click();}
      setPdfMessage('Full PDF includes the saved snag pins.');
    } catch(error){tab?.close();setPdfMessage(error instanceof Error ? error.message : 'The full PDF could not be prepared.');}
  }
  const positioned = useMemo(() => aspect && size.width ? layoutPins(points, size.width, size.height, aspect) : [], [points, size.width, size.height, aspect]);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({ mode: "" as "" | "pan" | "pinch", x: 0, y: 0, left: 0, top: 0, distance: 0, zoom: 1, focusX: 0, focusY: 0 });
  const pointerMoved = useRef(false);
  function mark(event: MouseEvent<HTMLButtonElement>) {
    if (!onMark || pointerMoved.current) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const point = imagePoint(event.clientX - bounds.left, event.clientY - bounds.top, bounds.width, bounds.height, aspect);
    if (point) onMark(Math.round(point.x * 10000), Math.round(point.y * 10000));
  }

  function pointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "touch" || (event.target as HTMLElement).closest(".map-pin")) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const scroll = scrollRef.current;
    if (!scroll) return;
    if (pointers.current.size === 1) {
      gesture.current = { mode: "pan", x: event.clientX, y: event.clientY, left: scroll.scrollLeft, top: scroll.scrollTop, distance: 0, zoom, focusX: 0, focusY: 0 };
    } else if (pointers.current.size === 2) {
      const [first, second] = [...pointers.current.values()];
      const bounds = scroll.getBoundingClientRect();
      const centreX = (first!.x + second!.x) / 2 - bounds.left;
      const centreY = (first!.y + second!.y) / 2 - bounds.top;
      gesture.current = {
        ...gesture.current,
        mode: "pinch",
        distance: Math.hypot(second!.x - first!.x, second!.y - first!.y),
        zoom,
        focusX: (scroll.scrollLeft + centreX) / zoom,
        focusY: (scroll.scrollTop + centreY) / zoom,
      };
    }
  }

  function pointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "touch" || !pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const scroll = scrollRef.current;
    if (!scroll) return;
    if (pointers.current.size >= 2) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const [first, second] = [...pointers.current.values()];
      const distance = Math.hypot(second!.x - first!.x, second!.y - first!.y);
      if (gesture.current.distance > 0) {
        const nextZoom = Math.max(1, Math.min(12, gesture.current.zoom * distance / gesture.current.distance));
        const bounds = scroll.getBoundingClientRect();
        const centreX = (first!.x + second!.x) / 2 - bounds.left;
        const centreY = (first!.y + second!.y) / 2 - bounds.top;
        setZoom(nextZoom);
        window.requestAnimationFrame(() => {
          scroll.scrollLeft = gesture.current.focusX * nextZoom - centreX;
          scroll.scrollTop = gesture.current.focusY * nextZoom - centreY;
        });
      }
      pointerMoved.current = true;
    } else if (gesture.current.mode === "pan") {
      const dx = event.clientX - gesture.current.x; const dy = event.clientY - gesture.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) { pointerMoved.current = true; event.currentTarget.setPointerCapture(event.pointerId); }
      scroll.scrollLeft = gesture.current.left - dx; scroll.scrollTop = gesture.current.top - dy;
    }
  }

  function pointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    if (!pointers.current.size) { gesture.current.mode = ""; window.setTimeout(() => { pointerMoved.current = false; }, 0); }
  }

  return <div className={`drawing-markup-shell${print ? " drawing-print" : ""}`}>
    {pdfMessage && !print && <small className="pdf-export-message" role="status">{pdfMessage}</small>}
    {!print && <div className="drawing-zoom-controls" aria-label="Drawing zoom controls">{leadingControls}<button aria-label="Zoom out" disabled={zoom <= 1} onClick={() => setZoom((value) => Math.max(1, value - .5))} type="button">−</button><strong>{Math.round(zoom * 100)}%</strong><button aria-label="Zoom in" disabled={zoom >= 12} onClick={() => setZoom((value) => Math.min(12, value + .5))} type="button">＋</button><button disabled={zoom === 1} onClick={() => setZoom(1)} type="button">Fit</button></div>}
    <div className="drawing-markup-scroll" onPointerDownCapture={pointerDown} onPointerMoveCapture={pointerMove} onPointerUpCapture={pointerUp} onPointerCancelCapture={pointerUp} ref={scrollRef}>
      <div ref={frameRef} className="drawing-markup-canvas" style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
        {drawing.contentType === "application/pdf" ? <PdfDrawingPreview drawing={drawing} pageNumber={page} onAspect={setAspect} onOpenFull={()=>void openFullPdf()} /> : <DrawingImage drawing={drawing} onAspect={setAspect} />}
        {onMark && <button className="drawing-mark-target" disabled={!aspect} aria-label="Place drawing reference pin" onClick={mark} type="button" />}
        <svg className="pin-leaders" width="100%" height="100%" viewBox={`0 0 ${size.width} ${size.height}`} preserveAspectRatio="none" aria-hidden="true">{positioned.map((pin) => <g key={pin.id}><line x1={pin.anchorX} y1={pin.anchorY} x2={pin.labelX} y2={pin.labelY} /><circle className="pin-ring" cx={pin.anchorX} cy={pin.anchorY} r="4.5" /><circle className="pin-dot" cx={pin.anchorX} cy={pin.anchorY} r="2" /></g>)}</svg>
        {positioned.map((pin) => {
          const style = { left: `${pin.labelX / size.width * 100}%`, top: `${pin.labelY / size.height * 100}%`, minWidth: pin.width };
          return onOpenPin ? <button key={pin.id} type="button" className="map-pin" style={style} aria-label={`Open snag ${pin.number}${pin.title ? `: ${pin.title}` : ""}`} onClick={(event) => { event.stopPropagation(); if (!pointerMoved.current) onOpenPin(pin.id); }}>{pin.number}</button> : <span key={pin.id} className="map-pin" style={style}>{pin.number}</span>;
        })}
        {onMark && <small>Tap the defect position to place or move the pin</small>}
      </div>
    </div>
  </div>;
}
